import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Download, Upload, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  employee_id: "", email: "", name: "", password: "", role: "employee",
  age: "", mobile: "", preferred_language: "English",
  country_id: "", project_id: "", center_ids: [], status: "active",
  visible_menus: [],
};

const CENTER_ADMIN_MENUS = [
  { key: "attendance",       label: "Attendance list" },
  { key: "monthly_view",     label: "Monthly View" },
  { key: "location_map",     label: "Location Map" },
  { key: "corrections",      label: "Corrections" },
  { key: "reports",          label: "Reports" },
  { key: "employees",        label: "Employees" },
  { key: "audit",            label: "Audit Log" },
];

export default function Employees() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [centers, setCenters] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = async () => {
    const [u, co, pr, ce] = await Promise.all([
      api.get("/users"), api.get("/countries"), api.get("/projects"), api.get("/centers"),
    ]);
    setItems(u.data); setCountries(co.data); setProjects(pr.data); setCenters(ce.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (u) => {
    setEditing(u);
    setForm({
      employee_id: u.employee_id, email: u.email, name: u.name, password: "",
      role: u.role, age: u.age || "", mobile: u.mobile || "",
      preferred_language: u.preferred_language || "English",
      country_id: u.country_id || "", project_id: u.project_id || "",
      center_ids: u.center_ids || [], status: u.status,
      visible_menus: u.visible_menus || [],
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, age: form.age ? Number(form.age) : null };
      if (!payload.password) delete payload.password;
      if (!payload.country_id) payload.country_id = null;
      if (!payload.project_id) payload.project_id = null;
      if (editing) await api.patch(`/users/${editing.id}`, payload);
      else await api.post("/users", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this user?")) return; await api.delete(`/users/${id}`); load(); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/users/bulk/template.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "users-bulk-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const submitBulk = async () => {
    if (!bulkFile) return toast.error("Choose a file first");
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      const { data } = await api.post("/users/bulk/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} user${data.created === 1 ? "" : "s"} created`);
        load();
      }
      if (data.errors > 0) {
        toast.warning(`${data.errors} row${data.errors === 1 ? "" : "s"} skipped — see details`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = items.filter(u => {
    const s = q.toLowerCase();
    return !s || u.name?.toLowerCase().includes(s) || u.employee_id?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  const canManage = user?.role === "super_admin";
  const projName = (id) => projects.find(p => p.id === id)?.name || "—";
  const countryName = (id) => countries.find(c => c.id === id)?.name || "—";
  const centerNames = (ids) => (ids || []).map(id => centers.find(c => c.id === id)?.name).filter(Boolean).join(", ") || "—";

  return (
    <>
      <PageHeader
        eyebrow="People"
        title="Employees"
        subtitle="Register and manage attendance-eligible users"
        actions={canManage && (
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              data-testid="bulk-template-btn"
              variant="outline"
              onClick={downloadTemplate}
              className="rounded-none border-border"
            >
              <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Template
            </Button>
            <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkFile(null); setBulkResult(null); } }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="bulk-upload-btn"
                  variant="outline"
                  className="rounded-none border-border"
                >
                  <Upload className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Bulk upload
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-card border-border max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display">Bulk create users</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                  <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                    <li>Click <span className="text-foreground">Template</span> above to download the pre-filled Excel.</li>
                    <li>Fill one user per row — pick <b>country</b>, <b>role</b>, <b>status</b>, and up to three <b>centers</b> from the dropdowns.</li>
                    <li>Come back here and upload the file. Errors are reported per row without stopping the batch.</li>
                  </ol>
                  <div className="border border-dashed border-border p-4">
                    <Label className="label-mono mb-2 block">Choose file (.xlsx or .csv)</Label>
                    <input
                      data-testid="bulk-file-input"
                      type="file"
                      accept=".xlsx,.csv"
                      onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
                      className="block w-full text-sm file:mr-3 file:border file:border-border file:bg-secondary file:px-3 file:py-1.5 file:font-mono-brand file:text-xs file:cursor-pointer"
                    />
                    {bulkFile && (
                      <div className="mt-2 text-xs font-mono-brand text-muted-foreground">
                        {bulkFile.name} · {Math.ceil(bulkFile.size / 1024)} KB
                      </div>
                    )}
                  </div>
                  {bulkResult && (
                    <div className="border border-border">
                      <div className="grid grid-cols-3 border-b border-border">
                        <div className="p-3">
                          <div className="label-mono">Total</div>
                          <div className="font-display text-2xl">{bulkResult.total_rows}</div>
                        </div>
                        <div className="p-3 border-l border-border">
                          <div className="label-mono text-emerald-300">Created</div>
                          <div className="font-display text-2xl text-emerald-300">{bulkResult.created}</div>
                        </div>
                        <div className="p-3 border-l border-border">
                          <div className="label-mono text-red-300">Errors</div>
                          <div className="font-display text-2xl text-red-300">{bulkResult.errors}</div>
                        </div>
                      </div>
                      <div className="max-h-72 overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-secondary/40 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2 label-mono">Row</th>
                              <th className="text-left px-3 py-2 label-mono">Employee ID</th>
                              <th className="text-left px-3 py-2 label-mono">Email</th>
                              <th className="text-left px-3 py-2 label-mono">Status</th>
                              <th className="text-left px-3 py-2 label-mono">Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkResult.results.map((r) => (
                              <tr key={r.row} className="border-t border-border">
                                <td className="px-3 py-1.5 font-mono-brand">{r.row}</td>
                                <td className="px-3 py-1.5 font-mono-brand">{r.employee_id || "—"}</td>
                                <td className="px-3 py-1.5">{r.email || "—"}</td>
                                <td className="px-3 py-1.5">
                                  {r.status === "created" ? (
                                    <span className="inline-flex items-center gap-1 text-emerald-300">
                                      <CheckCircle2 className="h-3.5 w-3.5" /> Created
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-red-300">
                                      <XCircle className="h-3.5 w-3.5" /> Error
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-muted-foreground">{r.reason || ""}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setBulkOpen(false)}
                    className="rounded-none border-border"
                  >
                    Close
                  </Button>
                  <Button
                    data-testid="bulk-submit-btn"
                    onClick={submitBulk}
                    disabled={!bulkFile || bulkBusy}
                    className="rounded-none btn-primary-brand"
                  >
                    {bulkBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                    Upload &amp; create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-employee-btn" onClick={startCreate} className="rounded-none btn-primary-brand">
                <Plus className="h-4 w-4 mr-1" /> New user
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">{editing ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-mono">Employee ID</Label><Input data-testid="emp-id-input" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="rounded-none font-mono-brand" disabled={!!editing} /></div>
                <div><Label className="label-mono">Full name</Label><Input data-testid="emp-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></div>
                <div><Label className="label-mono">Email</Label><Input data-testid="emp-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" /></div>
                <div><Label className="label-mono">Password {editing && "(leave blank to keep)"}</Label><Input data-testid="emp-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none" /></div>
                <div><Label className="label-mono">Age</Label><Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="rounded-none" /></div>
                <div><Label className="label-mono">Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="rounded-none" /></div>
                <div>
                  <Label className="label-mono">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="emp-role-select" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-card">
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="center_admin">Center Admin</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Language</Label>
                  <Input value={form.preferred_language} onChange={(e) => setForm({ ...form, preferred_language: e.target.value })} className="rounded-none" />
                </div>
                <div>
                  <Label className="label-mono">Country</Label>
                  <Select value={form.country_id || undefined} onValueChange={(v) => setForm({ ...form, country_id: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent className="rounded-none bg-card">
                      {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-mono">Project</Label>
                  <Select value={form.project_id || undefined} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent className="rounded-none bg-card">
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="label-mono">Assigned centers</Label>
                  <div className="border border-border p-2 max-h-36 overflow-auto grid grid-cols-2 gap-1">
                    {centers.map(c => (
                      <label key={c.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={form.center_ids.includes(c.id)}
                          onChange={(e) => {
                            const s = new Set(form.center_ids);
                            e.target.checked ? s.add(c.id) : s.delete(c.id);
                            setForm({ ...form, center_ids: Array.from(s) });
                          }}
                        />
                        {c.name}
                      </label>
                    ))}
                  </div>
                </div>

                {form.role === "center_admin" && (
                  <div className="col-span-2">
                    <Label className="label-mono">Visible admin menus</Label>
                    <div className="text-[10px] text-muted-foreground mb-1 font-mono-brand">
                      Leave all unchecked to show every default menu. Otherwise, only the ticked menus will appear in this admin's sidebar. Dashboard and My Attendance are always shown.
                    </div>
                    <div className="border border-border p-2 max-h-36 overflow-auto grid grid-cols-2 gap-1">
                      {CENTER_ADMIN_MENUS.map((m) => (
                        <label key={m.key} className="flex items-center gap-2 text-sm">
                          <input
                            data-testid={`menu-perm-${m.key}`}
                            type="checkbox"
                            checked={form.visible_menus.includes(m.key)}
                            onChange={(e) => {
                              const s = new Set(form.visible_menus);
                              e.target.checked ? s.add(m.key) : s.delete(m.key);
                              setForm({ ...form, visible_menus: Array.from(s) });
                            }}
                          />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className="label-mono">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-card">
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button data-testid="emp-save-btn" onClick={save} className="rounded-none btn-primary-brand">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      />

      <div className="p-8 space-y-4">
        <Input
          data-testid="employee-search"
          placeholder="Search name, ID, or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="rounded-none max-w-sm"
        />
        <div className="border border-border">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="label-mono">ID</TableHead>
                <TableHead className="label-mono">Name</TableHead>
                <TableHead className="label-mono">Role</TableHead>
                <TableHead className="label-mono">Country</TableHead>
                <TableHead className="label-mono">Project</TableHead>
                <TableHead className="label-mono">Centers</TableHead>
                <TableHead className="label-mono">Status</TableHead>
                {canManage && <TableHead className="label-mono w-16"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(u => (
                <TableRow key={u.id} className="border-border" data-testid={`employee-row-${u.employee_id}`}>
                  <TableCell className="font-mono-brand text-xs">{u.employee_id}</TableCell>
                  <TableCell>
                    <Link
                      to={`/admin/employees/${u.id}`}
                      data-testid={`employee-open-${u.employee_id}`}
                      className="hover:underline hover:text-[hsl(var(--accent))]"
                    >
                      {u.name}
                    </Link>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </TableCell>
                  <TableCell className="label-mono">{u.role.replace("_"," ")}</TableCell>
                  <TableCell>{countryName(u.country_id)}</TableCell>
                  <TableCell>{projName(u.project_id)}</TableCell>
                  <TableCell className="text-xs">{centerNames(u.center_ids)}</TableCell>
                  <TableCell><span className={u.status === "active" ? "text-emerald-400" : "text-red-400"}>{u.status}</span></TableCell>
                  {canManage && (
                    <TableCell className="flex gap-1">
                      <button onClick={() => startEdit(u)} className="p-1.5 border border-border hover:bg-secondary"><Pencil className="h-3 w-3" strokeWidth={1.5} /></button>
                      <button onClick={() => del(u.id)} className="p-1.5 border border-border hover:bg-secondary"><Trash2 className="h-3 w-3" strokeWidth={1.5} /></button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
